/**
 * InvoiceDocumentPreview — wspólny układ dokumentu faktury.
 *
 * Renderuje sam dokument (header → sumy), bez Dialog / Footer.
 * Używany w:
 *   - KSeF Inbox preview (`KsefInboxPage.jsx`)
 *   - Faktury preview (`InvoicesPage.jsx`)
 *
 * Kontrakt wejścia (`data`) zgodny z odpowiedzią `GET /api/ksef/inbox/{ksef_number}`:
 *   { invoice_number, ksef_number,
 *     seller_name, seller_nip, seller_address, seller_email, seller_phone,
 *     buyer_name, buyer_nip, buyer_address,
 *     invoice_date, service_date, due_date, currency, invoice_type,
 *     payment_method, bank_account,
 *     items[], net_amount, vat_amount, gross_amount,
 *     metadata_only, xml_fetch_error,
 *     parsed_tree, parsed_tree_status, parsed_tree_error   ← rozszerzenie FA(3) 2025
 *   }
 *
 * Zasady renderowania z `parsed_tree` (lossless FA(3)):
 *  1. NIE pokazujemy pustych pól ani sekcji.
 *  2. Wszystkie nowe sekcje warunkowe — pojawiają się TYLKO gdy dane istnieją.
 *  3. Backward compat: gdy `parsed_tree` = null (legacy XML / brak XML / błąd parsera),
 *     UI wygląda identycznie jak wcześniej (tylko flat pola).
 *  4. Zachowujemy istniejący styl — dodajemy informacje, nie zmieniamy layoutu.
 *
 * Dla faktur z modułu Faktury bez ksef_number, wrapper buduje obiekt
 * o tym samym kształcie z metadanych invoice + ustawia metadata_only=true.
 */
import { RefreshCw } from "lucide-react";
import {
  hasValue,
  pick,
  asList,
  asStr,
  formatRodzajFaktury,
  extractAdnotacje,
  extractBankAccounts,
  extractFaktorAccount,
  isZaplacona,
  formatAdres,
  extractGtu,
  extractProcedury,
} from "./fa3TreeSelectors";

const PAYMENT_FORMS = {
  "1": "Gotówka", "2": "Karta", "3": "Bon", "4": "Czek", "5": "Weksel",
  "6": "Przelew", "7": "Mobilna", "8": "Kredyt/Pożyczka", "9": "Faktoring", "10": "Inna",
};

// Kanoniczne wewnętrzne wartości metody płatności (angielskie, z backendu:
// services/payment_method_normalizer.py) → polskie etykiety dla UI.
// Bez tego string np. "transfer" wyciekał na fakturę zamiast "Przelew".
const PAYMENT_METHOD_LABELS = {
  transfer: "Przelew",
  cash: "Gotówka",
  card: "Karta",
  blik: "BLIK",
  online_payment: "Płatność online",
  online: "Płatność online",
  other_non_transfer: "Inna forma (nie-przelew)",
  mixed: "Mieszane",
  unknown: "Nieokreślona",
};

const formatMoney = (amount, currency = "PLN") => {
  const n = Number(amount || 0);
  return `${n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pl-PL", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return iso;
  }
};

const formatPaymentMethod = (code) => {
  if (code === null || code === undefined || code === "") return "—";
  const s = String(code).trim();
  // 1) Kod numeryczny KSeF FA(3) (np. "6" → "Przelew").
  if (PAYMENT_FORMS[s]) return PAYMENT_FORMS[s];
  // 2) Kanoniczna wartość wewnętrzna (np. "transfer" → "Przelew").
  const low = s.toLowerCase();
  if (PAYMENT_METHOD_LABELS[low]) return PAYMENT_METHOD_LABELS[low];
  // 3) Fallback — pokaż wartość jak jest (może być już po polsku).
  return s;
};

// TerminOpis wg XSD FA(3) to zwykły string, ale część wystawców zagnieżdża
// strukturę (<Ilosc>/<Jednostka>/<ZdarzeniePoczatkowe>). Bez tego pole
// renderowało "[object Object]". Zwracamy czytelny polski tekst.
const formatTerminOpis = (v) => {
  if (v == null) return null;
  if (typeof v !== "object") {
    const s = String(v).trim();
    return s.length > 0 ? s : null;
  }
  const ilosc = v.Ilosc != null ? String(v.Ilosc).trim() : "";
  const jednostka = v.Jednostka != null ? String(v.Jednostka).trim() : "";
  const zdarzenie = v.ZdarzeniePoczatkowe != null ? String(v.ZdarzeniePoczatkowe).trim() : "";
  const parts = [];
  const iloscJedn = [ilosc, jednostka].filter(Boolean).join(" ");
  if (iloscJedn) parts.push(iloscJedn);
  if (zdarzenie) parts.push(`od ${zdarzenie}`);
  if (parts.length > 0) return parts.join(" ");
  // Fallback ogólny: połącz wartości skalarne obiektu.
  const scalars = Object.values(v)
    .filter((x) => x != null && typeof x !== "object")
    .map((x) => String(x).trim())
    .filter(Boolean);
  return scalars.length > 0 ? scalars.join(", ") : null;
};

const DetailItem = ({ label, value, mono }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-gray-900 ${mono ? "font-mono text-xs break-all" : ""}`}>{value}</div>
  </div>
);

const SummaryLine = ({ label, value, bold }) => (
  <div className={`flex justify-between ${bold ? "text-base font-semibold border-t border-gray-200 pt-2" : "text-gray-700"}`}>
    <span>{label}</span>
    <span className="font-mono">{value}</span>
  </div>
);

// Etykieta jako pigułka (badge inline). Neutralny styl — nie zmieniamy palety.
const Pill = ({ children, testId }) => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-gray-100 text-gray-700 border border-gray-200"
    data-testid={testId}
  >
    {children}
  </span>
);

const InvoiceDocumentPreview = ({ data, fetchingXml = false }) => {
  if (!data) {
    return <div className="px-6 py-12 text-center text-gray-500">Brak danych do wyświetlenia.</div>;
  }

  const validMethod = data.payment_method && /^([1-9]|10)$/.test(String(data.payment_method));
  const validBank = data.bank_account && String(data.bank_account).replace(/\s/g, "").length >= 20;

  // ============================================================
  // Rozszerzone dane z parsed_tree (FA(3) 2025 lossless). Wszystkie
  // poniższe wyprowadzone wartości są ewentualnie null/[] gdy nie
  // ma danych w konkretnej fakturze — sekcje/pola nie są renderowane.
  // ============================================================
  const tree = data.parsed_tree || null;
  const fa = pick(tree, "Fa") || null;
  const podmiot1 = pick(tree, "Podmiot1") || null;
  const podmiot2 = pick(tree, "Podmiot2") || null;
  const stopka = pick(tree, "Stopka") || null;

  // Sprzedawca — dodatki (email/phone/KRS/REGON/BDO)
  const sellerEmailXml = asStr(pick(podmiot1, "DaneIdentyfikacyjne", "Email"))
    || asStr(pick(podmiot1, "DaneKontaktowe", "Email"));
  const sellerPhoneXml = asStr(pick(podmiot1, "DaneIdentyfikacyjne", "Telefon"))
    || asStr(pick(podmiot1, "DaneKontaktowe", "Telefon"));
  const sellerKrs = asStr(pick(podmiot1, "DaneIdentyfikacyjne", "KRS"));
  const sellerRegon = asStr(pick(podmiot1, "DaneIdentyfikacyjne", "REGON"));
  const sellerBdo = asStr(pick(podmiot1, "DaneIdentyfikacyjne", "BDO"));
  const sellerAdresXml = formatAdres(pick(podmiot1, "Adres"));

  // Nabywca — dodatki
  const buyerEmailXml = asStr(pick(podmiot2, "DaneIdentyfikacyjne", "Email"))
    || asStr(pick(podmiot2, "DaneKontaktowe", "Email"));
  const buyerPhoneXml = asStr(pick(podmiot2, "DaneIdentyfikacyjne", "Telefon"))
    || asStr(pick(podmiot2, "DaneKontaktowe", "Telefon"));
  const buyerKrs = asStr(pick(podmiot2, "DaneIdentyfikacyjne", "KRS"));
  const buyerRegon = asStr(pick(podmiot2, "DaneIdentyfikacyjne", "REGON"));
  const buyerBdo = asStr(pick(podmiot2, "DaneIdentyfikacyjne", "BDO"));
  const buyerAdresXml = formatAdres(pick(podmiot2, "Adres"));

  // Rodzaj faktury (rozwinięcie enum → PL label)
  const rodzajFaktury = asStr(pick(fa, "RodzajFaktury"));
  const rodzajFakturyLabel = formatRodzajFaktury(rodzajFaktury) || data.invoice_type || null;

  // Dokument — pola z Fa: NrZamowienia, DataZamowienia, WZ, P_6Od/P_6Do, korekta, opisy
  const nrZamowienia = asStr(pick(fa, "NrZamowienia"));
  const dataZamowienia = asStr(pick(fa, "DataZamowienia"));
  const nrWZ = asList(pick(fa, "WZ")).map(asStr).filter(Boolean);
  const p6Od = asStr(pick(fa, "P_6Od"));
  const p6Do = asStr(pick(fa, "P_6Do"));
  const przyczynaKorekty = asStr(pick(fa, "PrzyczynaKorekty"));
  const nrFaKorygowanej = asStr(pick(fa, "NrFaKorygowanej"));
  const daneFaKorygowanej = pick(fa, "DaneFaKorygowanej");
  const dodatkowyOpis = asStr(pick(fa, "DodatkowyOpis"))
    || asList(pick(fa, "DodatkowyOpis")).map(asStr).filter(Boolean).join("\n");
  const warunkiDostawy = asStr(pick(fa, "WarunkiTransakcji", "WarunkiDostawy"));
  const transport = asStr(pick(fa, "Transport"));

  // Adnotacje (P_16..P_23 itd.)
  const adnotacje = fa ? extractAdnotacje(fa) : [];

  // Stopka
  const stopkaInfo = asStr(pick(stopka, "Informacje"));
  const stopkaKrs = asStr(pick(stopka, "Rejestry", "KRS"));
  const stopkaRegon = asStr(pick(stopka, "Rejestry", "REGON"));
  const stopkaBdo = asStr(pick(stopka, "Rejestry", "BDO"));

  // Płatność rozszerzona
  const bankAccounts = fa ? extractBankAccounts(fa) : [];
  const faktorAccount = fa ? extractFaktorAccount(fa) : null;
  const opisPlatnosci = asStr(pick(fa, "Platnosc", "OpisPlatnosci"));
  const platnoscInna = asStr(pick(fa, "Platnosc", "PlatnoscInna"));
  // TerminPlatnosci może być listą (maxOccurs>1 wg XSD) — bierzemy pierwszy, ale zbieramy
  // opis z wszystkich jeśli różny.
  const terminList = asList(pick(fa, "Platnosc", "TerminPlatnosci"));
  const terminOpis = terminList.map((t) => formatTerminOpis(t?.TerminOpis)).filter(Boolean).join(", ") || null;
  const terminXml = terminList.map((t) => asStr(t?.Termin)).filter(Boolean).join(", ") || null;
  void terminXml; // rezerwowany fallback — obecnie używamy data.due_date (kompatybilność)
  const zaplacona = fa ? isZaplacona(fa) : false;
  const dataZaplaty = asStr(pick(fa, "Platnosc", "DataZaplaty"));
  const zaplaconoCzesc = asStr(pick(fa, "Platnosc", "ZaplaconoCzesc"));
  const doZaplaty = asStr(pick(fa, "Platnosc", "DoZaplaty"));
  const formaPlatnosciXml = asStr(pick(fa, "Platnosc", "FormaPlatnosci"));

  // Czy sekcja Płatność ma cokolwiek do pokazania (rozszerzone LUB stare pola)
  const hasExtendedPayment =
    bankAccounts.length > 0 ||
    !!faktorAccount ||
    hasValue(opisPlatnosci) ||
    hasValue(platnoscInna) ||
    hasValue(terminOpis) ||
    zaplacona ||
    hasValue(dataZaplaty) ||
    hasValue(zaplaconoCzesc) ||
    hasValue(doZaplaty);
  const paymentSectionVisible = validMethod || validBank || hasExtendedPayment || hasValue(formaPlatnosciXml);

  // Pozycje — czy jakakolwiek pozycja ma dodatkowe pola?
  const wiersze = fa ? asList(pick(fa, "FaWiersz")) : [];
  const wierszeByIndex = new Map();
  wiersze.forEach((w) => {
    const idx = asStr(w?.NrWierszaFa);
    if (idx) wierszeByIndex.set(idx, w);
  });
  // Zbiór pozycji z ich rozszerzonymi polami (indexed by lp for merge z data.items)
  const itemsExtras = (data.items || []).map((_, idx) => {
    // Preferuj match po NrWierszaFa === (idx+1); fallback do kolejności listy
    const w = wierszeByIndex.get(String(idx + 1)) || wiersze[idx] || null;
    if (!w) return null;
    return {
      indeks: asStr(w.Indeks),
      uuid: asStr(w.UU_ID),
      pkwiu: asStr(w.PKWiU),
      cn: asStr(w.CN),
      pkob: asStr(w.PKOB),
      gtin: asStr(w.GTIN),
      gtu: extractGtu(w),
      procedury: extractProcedury(w),
    };
  });
  const hasAnyItemExtras = itemsExtras.some(
    (e) => e && (hasValue(e.indeks) || hasValue(e.uuid) || hasValue(e.pkwiu)
      || hasValue(e.cn) || hasValue(e.pkob) || hasValue(e.gtin)
      || e.gtu.length > 0 || e.procedury.length > 0)
  );

  return (
    <div className="bg-gray-50 p-3 sm:p-6">
      <div
        className="bg-white shadow-sm border border-gray-200 rounded-md p-4 sm:p-8 space-y-6 text-sm"
        data-testid="invoice-document-preview"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 border-b border-gray-200 pb-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Krajowy System e-Faktur</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">Faktura</div>
          </div>
          <div className="sm:text-right">
            <div className="font-mono text-sm font-semibold text-gray-900 break-all">{data.invoice_number || "—"}</div>
            {data.ksef_number && (
              <div className="font-mono text-xs text-gray-500 mt-1 break-all">KSeF: {data.ksef_number}</div>
            )}
          </div>
        </div>

        {/* Auto-fetch indicator */}
        {fetchingXml && (
          <div
            className="bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded px-3 py-2 flex items-center gap-2"
            data-testid="invoice-preview-loading-xml"
          >
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            Pobieranie pełnego dokumentu z KSeF…
          </div>
        )}

        {/* Metadata-only / error banners */}
        {!fetchingXml && data.metadata_only && data.xml_fetch_error && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded px-3 py-2">
            Nie udało się pobrać pełnego XML: {data.xml_fetch_error}. Pokazujemy dostępne metadane.
          </div>
        )}

        {!fetchingXml && data.metadata_only && !data.xml_fetch_error && (
          <div
            className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded px-3 py-2"
            data-testid="invoice-preview-metadata-only"
          >
            Podgląd zbudowany z dostępnych danych faktury. Pełny XML nie jest dostępny.
          </div>
        )}

        {/* Legacy schema banner (parsed_tree niedostępny bo XML < FA(3) 2025) */}
        {!fetchingXml && data.parsed_tree_status === "legacy" && (
          <div
            className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded px-3 py-2"
            data-testid="invoice-preview-legacy-xml"
          >
            Faktura w starym formacie (FA(2) / 2023). Pokazujemy podstawowe pola — rozszerzone dane FA(3) 2025 nie są dostępne.
          </div>
        )}

        {/* Sprzedawca / Nabywca */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div data-testid="invoice-preview-seller">
            <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Sprzedawca</div>
            <div className="text-gray-900 font-medium break-words">{data.seller_name || "—"}</div>
            <div className="text-gray-700">NIP: {data.seller_nip || "—"}</div>
            {(data.seller_address || sellerAdresXml) && (
              <div className="text-gray-700 mt-1 whitespace-pre-line break-words">
                {data.seller_address || sellerAdresXml}
              </div>
            )}
            {(sellerKrs || sellerRegon || sellerBdo) && (
              <div className="text-gray-600 mt-2 flex flex-wrap gap-1.5" data-testid="seller-registers">
                {sellerKrs && <Pill testId="seller-krs">KRS: {sellerKrs}</Pill>}
                {sellerRegon && <Pill testId="seller-regon">REGON: {sellerRegon}</Pill>}
                {sellerBdo && <Pill testId="seller-bdo">BDO: {sellerBdo}</Pill>}
              </div>
            )}
            {(data.seller_email || sellerEmailXml) && (
              <div className="text-gray-700 mt-1 break-all" data-testid="seller-email">
                {data.seller_email || sellerEmailXml}
              </div>
            )}
            {(data.seller_phone || sellerPhoneXml) && (
              <div className="text-gray-700" data-testid="seller-phone">
                {data.seller_phone || sellerPhoneXml}
              </div>
            )}
          </div>
          <div data-testid="invoice-preview-buyer">
            <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Nabywca</div>
            <div className="text-gray-900 font-medium break-words">{data.buyer_name || "—"}</div>
            <div className="text-gray-700">NIP: {data.buyer_nip || "—"}</div>
            {(data.buyer_address || buyerAdresXml) && (
              <div className="text-gray-700 mt-1 whitespace-pre-line break-words">
                {data.buyer_address || buyerAdresXml}
              </div>
            )}
            {(buyerKrs || buyerRegon || buyerBdo) && (
              <div className="text-gray-600 mt-2 flex flex-wrap gap-1.5" data-testid="buyer-registers">
                {buyerKrs && <Pill testId="buyer-krs">KRS: {buyerKrs}</Pill>}
                {buyerRegon && <Pill testId="buyer-regon">REGON: {buyerRegon}</Pill>}
                {buyerBdo && <Pill testId="buyer-bdo">BDO: {buyerBdo}</Pill>}
              </div>
            )}
            {buyerEmailXml && (
              <div className="text-gray-700 mt-1 break-all" data-testid="buyer-email">
                {buyerEmailXml}
              </div>
            )}
            {buyerPhoneXml && (
              <div className="text-gray-700" data-testid="buyer-phone">
                {buyerPhoneXml}
              </div>
            )}
          </div>
        </div>

        {/* Szczegóły */}
        <div>
          <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Szczegóły</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <DetailItem label="Data wystawienia" value={formatDate(data.invoice_date)} />
            {data.service_date && !p6Od && (
              <DetailItem label="Data sprzedaży" value={formatDate(data.service_date)} />
            )}
            {(p6Od || p6Do) && (
              <DetailItem
                label="Okres sprzedaży"
                value={`${p6Od ? formatDate(p6Od) : "—"} — ${p6Do ? formatDate(p6Do) : "—"}`}
              />
            )}
            <DetailItem label="Waluta" value={data.currency || "PLN"} />
            {data.due_date && <DetailItem label="Termin płatności" value={formatDate(data.due_date)} />}
            {rodzajFakturyLabel && (
              <DetailItem label="Rodzaj faktury" value={rodzajFakturyLabel} />
            )}
          </div>
        </div>

        {/* Płatność */}
        {paymentSectionVisible && (
          <div data-testid="invoice-preview-payment">
            <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Płatność</div>

            {/* Status opłacenia (badge — dyskretny, obok reszty pól) */}
            {(zaplacona || hasValue(zaplaconoCzesc) || hasValue(doZaplaty)) && (
              <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" data-testid="payment-status">
                {zaplacona && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Opłacona
                    {dataZaplaty && <span className="ml-1 text-emerald-600">· {formatDate(dataZaplaty)}</span>}
                  </span>
                )}
                {!zaplacona && hasValue(zaplaconoCzesc) && Number(zaplaconoCzesc) > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                    Częściowo opłacona · {formatMoney(zaplaconoCzesc, data.currency)}
                  </span>
                )}
                {hasValue(doZaplaty) && !zaplacona && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                    Do zapłaty · <span className="ml-1 font-mono">{formatMoney(doZaplaty, data.currency)}</span>
                  </span>
                )}
              </div>
            )}

            {/* Podstawowe pola płatności */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {(validMethod || hasValue(formaPlatnosciXml)) && (
                <DetailItem
                  label="Forma płatności"
                  value={formatPaymentMethod(data.payment_method || formaPlatnosciXml)}
                />
              )}
              {hasValue(platnoscInna) && (
                <DetailItem label="Inna forma płatności" value={platnoscInna} />
              )}
              {hasValue(terminOpis) && (
                <DetailItem label="Termin (opis)" value={terminOpis} />
              )}
              {/* Kompatybilność: pojedynczy bank_account jeśli nie mamy rozszerzonych z XML */}
              {validBank && bankAccounts.length === 0 && (
                <DetailItem label="Rachunek bankowy" value={data.bank_account} mono />
              )}
              {hasValue(opisPlatnosci) && (
                <DetailItem label="Opis płatności" value={opisPlatnosci} />
              )}
            </div>

            {/* Rachunki bankowe rozszerzone (z parsed_tree). Wyświetlamy każdy z SWIFT/nazwą/opisem */}
            {bankAccounts.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="payment-bank-accounts">
                {bankAccounts.map((rb, idx) => (
                  <div key={idx} className="border border-gray-200 rounded p-2 bg-gray-50/50">
                    <div className="text-[11px] uppercase text-gray-500 mb-1">
                      Rachunek bankowy{bankAccounts.length > 1 ? ` #${idx + 1}` : ""}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      {rb.nrRb && <span className="font-mono text-xs text-gray-900 break-all">{rb.nrRb}</span>}
                      {rb.swift && <Pill testId={`bank-swift-${idx}`}>SWIFT: {rb.swift}</Pill>}
                      {rb.nazwaBanku && <span className="text-gray-700 text-xs">{rb.nazwaBanku}</span>}
                      {rb.opisRachunku && <span className="text-gray-500 text-xs italic">{rb.opisRachunku}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Rachunek faktora */}
            {faktorAccount && (
              <div className="mt-3 border border-gray-200 rounded p-2 bg-gray-50/50" data-testid="payment-factor-account">
                <div className="text-[11px] uppercase text-gray-500 mb-1">Rachunek faktora</div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  {faktorAccount.nrRb && (
                    <span className="font-mono text-xs text-gray-900 break-all">{faktorAccount.nrRb}</span>
                  )}
                  {faktorAccount.swift && <Pill>SWIFT: {faktorAccount.swift}</Pill>}
                  {faktorAccount.nazwaBanku && (
                    <span className="text-gray-700 text-xs">{faktorAccount.nazwaBanku}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dokument — nowa sekcja, tylko gdy mamy jakiekolwiek dane */}
        {(hasValue(nrZamowienia) || hasValue(dataZamowienia) || nrWZ.length > 0
          || hasValue(przyczynaKorekty) || hasValue(nrFaKorygowanej) || !!daneFaKorygowanej
          || hasValue(dodatkowyOpis) || hasValue(warunkiDostawy) || hasValue(transport)
          || adnotacje.length > 0
          || hasValue(stopkaInfo) || hasValue(stopkaKrs) || hasValue(stopkaRegon) || hasValue(stopkaBdo)) && (
          <div data-testid="invoice-preview-document">
            <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Dokument</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {hasValue(nrZamowienia) && <DetailItem label="Nr zamówienia" value={nrZamowienia} />}
              {hasValue(dataZamowienia) && <DetailItem label="Data zamówienia" value={formatDate(dataZamowienia)} />}
              {nrWZ.length > 0 && (
                <DetailItem label={nrWZ.length > 1 ? "Nr WZ" : "Nr WZ"} value={nrWZ.join(", ")} />
              )}
              {hasValue(warunkiDostawy) && <DetailItem label="Warunki dostawy" value={warunkiDostawy} />}
              {hasValue(transport) && <DetailItem label="Transport" value={transport} />}
              {hasValue(nrFaKorygowanej) && (
                <DetailItem label="Nr faktury korygowanej" value={nrFaKorygowanej} mono />
              )}
              {daneFaKorygowanej && typeof daneFaKorygowanej === "object" && (
                <>
                  {asStr(daneFaKorygowanej.DataWystFaKorygowanej) && (
                    <DetailItem
                      label="Data wyst. faktury korygowanej"
                      value={formatDate(daneFaKorygowanej.DataWystFaKorygowanej)}
                    />
                  )}
                  {asStr(daneFaKorygowanej.NrFaKorygowanej) && !nrFaKorygowanej && (
                    <DetailItem label="Nr faktury korygowanej" value={daneFaKorygowanej.NrFaKorygowanej} mono />
                  )}
                </>
              )}
            </div>
            {hasValue(przyczynaKorekty) && (
              <div className="mt-3">
                <div className="text-xs text-gray-500">Przyczyna korekty</div>
                <div className="text-gray-900 whitespace-pre-line">{przyczynaKorekty}</div>
              </div>
            )}
            {hasValue(dodatkowyOpis) && (
              <div className="mt-3">
                <div className="text-xs text-gray-500">Dodatkowy opis</div>
                <div className="text-gray-900 whitespace-pre-line">{dodatkowyOpis}</div>
              </div>
            )}
            {adnotacje.length > 0 && (
              <div className="mt-3">
                <div className="text-xs text-gray-500 mb-1">Adnotacje podatkowe</div>
                <div className="flex flex-wrap gap-1.5" data-testid="invoice-adnotacje">
                  {adnotacje.map((a) => (
                    <Pill key={a.key} testId={`adnotacja-${a.key}`}>
                      {a.label}
                    </Pill>
                  ))}
                </div>
              </div>
            )}
            {(hasValue(stopkaInfo) || hasValue(stopkaKrs) || hasValue(stopkaRegon) || hasValue(stopkaBdo)) && (
              <div className="mt-3">
                <div className="text-xs text-gray-500 mb-1">Stopka faktury</div>
                {hasValue(stopkaInfo) && (
                  <div className="text-gray-900 text-xs whitespace-pre-line break-words">{stopkaInfo}</div>
                )}
                {(hasValue(stopkaKrs) || hasValue(stopkaRegon) || hasValue(stopkaBdo)) && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {hasValue(stopkaKrs) && <Pill>KRS: {stopkaKrs}</Pill>}
                    {hasValue(stopkaRegon) && <Pill>REGON: {stopkaRegon}</Pill>}
                    {hasValue(stopkaBdo) && <Pill>BDO: {stopkaBdo}</Pill>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Pozycje */}
        {data.items && data.items.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Pozycje ({data.items.length})</div>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-xs border border-gray-200 min-w-[640px]">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2 w-8">Lp</th>
                    <th className="text-left px-3 py-2">Nazwa</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">J.m.</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">Ilość</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">Cena netto</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">Wart. netto</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">VAT %</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">Wart. brutto</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it, idx) => {
                    const extras = hasAnyItemExtras ? itemsExtras[idx] : null;
                    const hasExtras = extras && (
                      hasValue(extras.indeks) || hasValue(extras.uuid) || hasValue(extras.pkwiu)
                      || hasValue(extras.cn) || hasValue(extras.pkob) || hasValue(extras.gtin)
                      || extras.gtu.length > 0 || extras.procedury.length > 0
                    );
                    return (
                      <tr key={idx} className="border-t border-gray-200">
                        <td className="px-3 py-2 text-gray-500 align-top">{idx + 1}</td>
                        <td className="px-3 py-2 align-top">
                          <div>{it.name || "—"}</div>
                          {hasExtras && (
                            <div className="flex flex-wrap gap-1 mt-1" data-testid={`item-${idx}-extras`}>
                              {extras.gtu.map((g) => (
                                <Pill key={g} testId={`item-${idx}-${g}`}>{g}</Pill>
                              ))}
                              {extras.procedury.map((p) => (
                                <Pill key={`proc-${p}`}>{p}</Pill>
                              ))}
                              {hasValue(extras.pkwiu) && <Pill>PKWiU: {extras.pkwiu}</Pill>}
                              {hasValue(extras.cn) && <Pill>CN: {extras.cn}</Pill>}
                              {hasValue(extras.pkob) && <Pill>PKOB: {extras.pkob}</Pill>}
                              {hasValue(extras.gtin) && <Pill>GTIN: {extras.gtin}</Pill>}
                              {hasValue(extras.indeks) && <Pill>Indeks: {extras.indeks}</Pill>}
                              {hasValue(extras.uuid) && (
                                <span className="font-mono text-[10px] text-gray-400 break-all">
                                  {extras.uuid}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600 align-top">{it.unit || "—"}</td>
                        <td className="px-3 py-2 text-right align-top">{it.quantity || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono align-top">
                          {it.unit_price ? formatMoney(it.unit_price, data.currency) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono align-top">
                          {it.net_value ? formatMoney(it.net_value, data.currency) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right align-top">{it.vat_rate || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono align-top">
                          {it.gross_value ? formatMoney(it.gross_value, data.currency) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Podsumowanie */}
        <div className="border-t border-gray-200 pt-4 flex justify-end">
          <div className="w-full md:w-72 space-y-1 text-sm">
            <SummaryLine label="Netto" value={formatMoney(data.net_amount, data.currency)} />
            <SummaryLine label="VAT" value={formatMoney(data.vat_amount, data.currency)} />
            <SummaryLine label="Brutto" value={formatMoney(data.gross_amount, data.currency)} bold />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDocumentPreview;
